import * as React from 'react';
import {
  Key,
  Eye,
  EyeOff,
  Loader2,
  Check,
  Save,
  Languages,
  Sparkles,
} from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Card, CardContent, Button } from '@/components/ui';

export default function SettingsPage() {
  // Google Translate settings
  const [googleApiKey, setGoogleApiKey] = React.useState('');
  const [showGoogleApiKey, setShowGoogleApiKey] = React.useState(false);

  // OpenAI settings
  const [openaiApiKey, setOpenaiApiKey] = React.useState('');
  const [showOpenaiApiKey, setShowOpenaiApiKey] = React.useState(false);
  const [openaiTextModel, setOpenaiTextModel] = React.useState('gpt-4');
  const [openaiImageModel, setOpenaiImageModel] = React.useState('dall-e-3');

  // UI states
  const [isSaving, setIsSaving] = React.useState(false);
  const [saveSuccess, setSaveSuccess] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setIsLoading(true);

      // Load Google Translate API Key
      const googleResponse = await api.getGoogleTranslateApiKey();
      if (googleResponse.apiKey) {
        setGoogleApiKey(googleResponse.apiKey);
      }

      // Load OpenAI settings
      const openaiKeyResponse = await api.getOpenAIApiKey();
      if (openaiKeyResponse.apiKey) {
        setOpenaiApiKey(openaiKeyResponse.apiKey);
      }

      const openaiTextModelResponse = await api.getOpenAITextModel();
      if (openaiTextModelResponse.model) {
        setOpenaiTextModel(openaiTextModelResponse.model);
      }

      const openaiImageModelResponse = await api.getOpenAIImageModel();
      if (openaiImageModelResponse.model) {
        setOpenaiImageModel(openaiImageModelResponse.model);
      }
    } catch (err) {
      console.error('Erro ao carregar configurações:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setSaveSuccess(false);

      // Save all settings
      await Promise.all([
        api.setGoogleTranslateApiKey(googleApiKey),
        api.setOpenAIApiKey(openaiApiKey),
        api.setOpenAITextModel(openaiTextModel),
        api.setOpenAIImageModel(openaiImageModel),
      ]);

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      const apiError = err as ApiError;
      alert(apiError.message || 'Erro ao salvar configurações');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Configurações</h1>
          <p className="text-gray-500 mt-1">
            Configure as APIs e integrações do sistema
          </p>
        </div>
        <Card>
          <CardContent className="p-12">
            <div className="flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-[#0057FF]" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Configurações</h1>
          <p className="text-gray-500 mt-1">
            Configure as APIs e integrações do sistema
          </p>
        </div>
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Salvando...
            </>
          ) : saveSuccess ? (
            <>
              <Check className="h-4 w-4 mr-2" />
              Salvo!
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Salvar Todas
            </>
          )}
        </Button>
      </div>

      {/* Google Translate Configuration */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-50 rounded-lg">
              <Languages className="h-6 w-6 text-[#0057FF]" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">
                Google Cloud Translation
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                Configure a chave da API do Google Cloud Translation para habilitar tradução automática
              </p>
              <div className="relative">
                <input
                  type={showGoogleApiKey ? 'text' : 'password'}
                  placeholder="Insira a chave da API do Google Tradutor"
                  value={googleApiKey}
                  onChange={(e) => setGoogleApiKey(e.target.value)}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF]"
                />
                <button
                  type="button"
                  onClick={() => setShowGoogleApiKey(!showGoogleApiKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showGoogleApiKey ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Obtenha sua chave em{' '}
                <a
                  href="https://console.cloud.google.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#0057FF] hover:underline"
                >
                  Google Cloud Console
                </a>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* OpenAI Configuration */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-purple-50 rounded-lg">
              <Sparkles className="h-6 w-6 text-purple-600" />
            </div>
            <div className="flex-1 space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-1">
                  OpenAI
                </h2>
                <p className="text-sm text-gray-600">
                  Configure a chave da API da OpenAI e os modelos para geração de conteúdo
                </p>
              </div>

              {/* API Key */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Chave da API
                </label>
                <div className="relative">
                  <input
                    type={showOpenaiApiKey ? 'text' : 'password'}
                    placeholder="sk-..."
                    value={openaiApiKey}
                    onChange={(e) => setOpenaiApiKey(e.target.value)}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowOpenaiApiKey(!showOpenaiApiKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showOpenaiApiKey ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Obtenha sua chave em{' '}
                  <a
                    href="https://platform.openai.com/api-keys"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#0057FF] hover:underline"
                  >
                    OpenAI Platform
                  </a>
                </p>
              </div>

              {/* Text Model */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Modelo para Geração de Texto
                </label>
                <input
                  type="text"
                  placeholder="gpt-4, gpt-3.5-turbo, gpt-4-turbo..."
                  value={openaiTextModel}
                  onChange={(e) => setOpenaiTextModel(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF]"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Exemplo: gpt-4, gpt-4-turbo, gpt-3.5-turbo
                </p>
              </div>

              {/* Image Model */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Modelo para Geração de Imagem
                </label>
                <input
                  type="text"
                  placeholder="dall-e-3, dall-e-2..."
                  value={openaiImageModel}
                  onChange={(e) => setOpenaiImageModel(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0057FF]/20 focus:border-[#0057FF]"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Exemplo: dall-e-3, dall-e-2
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
